/**
 * Utilities for timing and logging to data layer
 *
 * @module
 */
import events from "@trinitymirrordigital/chameleon-core/src/js/events";
import { getFeatureValue } from "./configUtilities.js";
import { logFeature } from "./logging.js";
import * as nested from "nested-property";
import debounce from "lodash/debounce";

const flog = (...args) => logFeature("dataLayerUtilities", ...args);
const UPDATE_DELAY_SLOW = 5000;
const UPDATE_DELAY_FAST = 100;

let breakpointChanged = false;

const src = document.currentScript?.src ?? "";
const currentscript = src.substring(
  src.lastIndexOf("/") + 1,
  src.lastIndexOf("?")
);
flog("script", currentscript);

/**
 * Convert dash-separated strings to camelCase convention
 * @function camelCase
 * @param {string} input Value to convert
 * @returns {string} An string with no-dashed converted to camelCase convention
 */
function toCamelCase(input) {
  return input.toLowerCase().replace(/-(.)/g, function (match, output) {
    return output.toUpperCase();
  });
}

/**
 * Returns a Number representing the amount of milliseconds elapsed since a reference instant.
 * @function getPerformanceTime
 * @private
 * @returns {Number} A TimeStamp, measured in milliseconds
 */
function getPerformanceTime() {
  return window.performance ? window.performance.now() : Date.now();
}

/**
 * Gather performance data in the client side related to the Navigation Timing API
 * @function getPerformanceStats
 * @returns {Object} Object that contains the type of events that occurs in the given browsing context.
 */
function getPerformanceStats() {
  let timing = window.performance.timing;
  return {
    dns: timing.domainLookupEnd - timing.domainLookupStart,
    request: timing.responseStart - timing.requestStart,
    response: timing.responseEnd - timing.responseStart,
    tcp: timing.connectEnd - timing.connectStart,
  };
}

/**
 * Calculate a new average by adding a new value to an existing average
 * @param {number} previous The existing average to recalculate.
 * @param {number} additional The new value to include in the average calculation
 * @param {number} count The number of values that the existing average is calculated from
 * @returns {number}
 */
function calculateNewAverage(previous, additional, count) {
  // multiplies the 'previous' average by the number of values it was calculated from to get the 'previous' total sum of values
  // adds the new value to include in the average, and divides by the new number of values (previous plus one)
  return Math.round(((previous || 0) * count + additional) / (count + 1));
}

/**
 * Given a component type, it modifies the average connection time to include the one supplied
 * @function updateComponentAvg
 * @param componentType {string} The name of the component - e.g. taboola
 * @param connectTime {Number} The connection time to add the to the average
 * @param responseEnd {Number} The response end time to add the to the average
 * @param viewable {Number} 1 if the advert is viewable, 0 if not.
 * @param [timers] {Object} full set of object timers
 */
function updateComponentAvg(
  componentType,
  connectTime,
  responseEnd,
  viewable,
  timers
) {
  let consolidatedNode = window.commercialData.performanceReport.consolidated,
    newAvgConnect = connectTime,
    maxConnectTime = connectTime,
    numberOfComponents = 1,
    totalConnectTime = connectTime,
    newAvgEnd = responseEnd,
    maxEndTime = responseEnd,
    prevNumberOfComponents = 0,
    totalEndTime = responseEnd,
    totalViewability = viewable,
    storeEndTimes = window.performance && responseEnd > 0,
    avgViewability = viewable;

  if (consolidatedNode[componentType]) {
    const data = consolidatedNode[componentType].data;

    avgViewability = data.viewable;
    totalConnectTime = data.totalConnectTime;
    numberOfComponents = data.numberOfComponents + 1;
    maxConnectTime = data.maxConnectTime;
    prevNumberOfComponents = data.numberOfComponents;

    maxConnectTime = Math.max(maxConnectTime, connectTime);

    totalConnectTime = totalConnectTime + connectTime;
    newAvgConnect = totalConnectTime / numberOfComponents;

    totalViewability = prevNumberOfComponents * avgViewability + viewable;
    avgViewability = totalViewability / numberOfComponents;

    if (consolidatedNode[componentType] && storeEndTimes) {
      totalEndTime = data.totalEndTime;
      maxEndTime = data.maxEndTime;

      maxEndTime = Math.max(maxEndTime, responseEnd);

      totalEndTime = totalEndTime + responseEnd;
      newAvgEnd = totalEndTime / numberOfComponents;
    }
  } else {
    consolidatedNode[componentType] = {
      data: {},
    };
  }

  consolidatedNode[componentType].data.connectTime = newAvgConnect;
  consolidatedNode[componentType].data.totalConnectTime = totalConnectTime;
  consolidatedNode[componentType].data.maxConnectTime = maxConnectTime;
  consolidatedNode[componentType].data.numberOfComponents = numberOfComponents;
  consolidatedNode[componentType].data.viewable = avgViewability;

  // add new timer set, using generic average calculation
  Object.keys(timers).forEach((timer) => {
    if (timer.indexOf("To") !== -1) {
      consolidatedNode[componentType].data[timer] = calculateNewAverage(
        consolidatedNode[componentType].data[timer],
        timers[timer],
        prevNumberOfComponents
      );
    }
  });

  if (storeEndTimes) {
    consolidatedNode[componentType].data.averageEndTime = newAvgEnd;
    consolidatedNode[componentType].data.maxEndTime = maxEndTime;
    consolidatedNode[componentType].data.totalEndTime = totalEndTime;
  }
}

/**
 * Update the viewability value for a component
 *
 * @function updateComponentAvgViewability
 * @param componentType {String} {The type to update
 * @param viewable {1|0} The value to add the average
 */
function updateComponentAvgViewability(componentType, viewable) {
  let consolidatedNode = window.commercialData.performanceReport.consolidated,
    avgViewability = viewable,
    numberOfComponents,
    totalViewability;

  if (consolidatedNode[componentType]) {
    avgViewability = consolidatedNode[componentType].data.viewable;
    numberOfComponents =
      consolidatedNode[componentType].data.numberOfComponents;

    totalViewability = numberOfComponents * avgViewability + viewable;
    avgViewability = totalViewability / numberOfComponents;
  }

  consolidatedNode[componentType].data.viewable = avgViewability;
}

/**
 * Calculate the total time required to load a component in the application - Extend DataLayer with new values
 * @function performanceTimeReport
 * @param {Object} obj The node where the performance time has been set and its completed
 * @returns {Object} An object containing latency-related performance information per node
 */
function performanceTimeReport(obj) {
  if (getFeatureValue("reduceJunkInDataLayer", "features", "bool")) {
    return;
  }
  flog("performanceTimeReport", obj?.id);
  const attrs = obj.dataset;

  let component = attrs.type,
    responseStart = parseInt(attrs.responseStart),
    responseEnd = parseInt(attrs.responseEnd),
    requested = parseInt(attrs.requested),
    timeReport,
    type = toCamelCase(obj.id);

  let connectTime = parseInt((responseEnd - responseStart).toFixed(2));
  let viewable = attrs.viewable ? 1 : 0;

  const injectedToRequested = Math.round(requested - responseStart);
  const requestedToReceived = Math.round(responseEnd - requested);

  timeReport = {
    connectTime,
    responseEnd: responseEnd,
    responseStart: responseStart,
    viewable,
    // WEBX-1201 - add a whole new set to maintain backwards compatibility
    startToInjected: responseStart,
    startToRequested: requested,
    injectedToRequested: injectedToRequested,
    injectedToReceived: connectTime,
    requestedToReceived: requestedToReceived,
    startToReceived: responseEnd,
  };

  // add any custom, type-specific timers (all prefixed with data-timer)
  Object.keys(attrs).forEach((attr) => {
    if (attr.substr(0, 5) === "timer") {
      const timer = parseInt(attrs[attr]);
      timeReport["startTo" + attr.substr(5)] = timer;
      timeReport["requestedTo" + attr.substr(5)] = Math.round(
        timer - requested
      );
      timeReport[
        attr.substr(5, 1).toLowerCase() + attr.substr(6) + "ToReceived"
      ] = Math.round(responseEnd - timer);
    }
  });

  // Object with elements where the performance time has been calculated
  window.commercialData.performanceReport.components[type] = {
    data: timeReport,
  };

  // Can only do this if the responsEndTime was generated using the performance API (which counts from  beginning of page nav)
  updateComponentAvg(
    component,
    connectTime,
    window.performance ? responseEnd : 0,
    viewable,
    timeReport
  );

  updateDataLayer(true);

  return window.commercialData;
}

/**
 * Update the data layer to state whether an advert has been marked as viewable or not.
 *
 * @function updateViewability
 * @param obj {Object} The object to update
 */
function updateViewability(obj) {
  let viewable = obj.dataset.viewable ? 1 : 0;

  updateComponent(obj.id, "viewable", viewable);
  updateComponentAvgViewability(obj.dataset.type, viewable);
}

/**
 * Update a component in the data layer with new data
 *
 * @param objId {String} The ID of the object being updated
 * @param key {String} The key of the piece of data being updated
 * @param value {*} The value of new piece of data
 * @returns {Object} the updated data layer
 */
function updateComponent(objId, key, value) {
  flog("updateComponent", ...arguments);
  let type = toCamelCase(objId);

  window.commercialData.performanceReport.components[type].data[key] = value;
  updateDataLayer(true);

  return window.commercialData;
}

/**
 * Update the data layer with commercial data   *
 * @function updateDataLayer
 * @private
 */
function updateDataLayer(canItWait = false) {
  if (getFeatureValue("debounceDataLayer", "features", "bool")) {
    flog(
      "updateDataLayer, debounce:",
      canItWait ? UPDATE_DELAY_SLOW : UPDATE_DELAY_FAST
    );
    canItWait ? updateDebouncedSlow() : updateDebouncedFast();
  } else {
    update();
  }
}

function update() {
  flog("updateDataLayer NOW", currentscript);
  const data = {
    commercialData: window.commercialData,
  };
  if (window.TMDATA) {
    window.TMDATA.update(data);
  } else {
    events.listen(document.body, "dataLayer.ready", (e) => {
      e.data.update(data);
    });
  }
}

const updateDebouncedSlow = (() => {
  return debounce(update, UPDATE_DELAY_SLOW);
})();

const updateDebouncedFast = (() => {
  return debounce(update, UPDATE_DELAY_FAST);
})();

/**
 * Push custom event and data to the data layer
 * @function triggerEvent
 * @private
 */
export function triggerEvent(event, data = null) {
  flog("triggerEvent");
  const dataLayerReady = window.dataLayer?.push;
  const push = () => {
    window.dataLayer?.push(data ? { event, data } : { event });
  };

  dataLayerReady
    ? push()
    : events.listen(document.body, "dataLayer.ready", push);
}

/**
 * Initialise an element in the DOM for performance timing
 * - sets data on the element for type and timing start timestamp
 *
 * @param {Object} el - the element to initialise
 * @param {String} elType - the type of element being timed (e.g seldon, gpt, taboola...)
 * @param {String} startTimestamp - optional, the start timestamp for timing on this element
 *
 */
function initialiseElementTiming(el, elType, startTimestamp) {
  let timing = startTimestamp ? startTimestamp : getPerformanceTime();

  if (!el) {
    flog("[ERROR] The element to set timing was not found on the page.");
    return;
  }

  el.setAttribute("data-response-start", timing);
  el.setAttribute("data-type", elType);
}

/**
 * Set a custom timer, timestamped to 'now'
 * @param {Object} el The DOM element being measured
 * @param {String} name The name of the timer
 * @returns {Object} The modified DOM element
 */
function setCustomTimer(el, name) {
  el.setAttribute("data-timer-" + name, getPerformanceTime());
  return el;
}

/**
 * Completes the timing of an element, recordng the results
 * @function finaliseElementTiming
 * @param {Object} el - the element being timed
 */
function finaliseElementTiming(el) {
  if (!el) {
    flog("[ERROR] The element to set timing was not found on the page.");
    return;
  }

  el.setAttribute("data-response-end", getPerformanceTime());
  if (!breakpointChanged) {
    performanceTimeReport(el);
  }
}

/**
 * Initialise the timing, recording on datalayer
 *
 * @function initialiseDatalayerTiming
 * @param {Object} path - datalayer path from info
 * @param {String} startTimestamp - optional, the start timestamp for timing on this element
 *
 */
function initialiseDatalayerTiming(path, startTimestamp) {
  let timing = startTimestamp ? startTimestamp : getPerformanceTime();

  if (!path) {
    flog("[ERROR] The path to set timing was not found.");
    return;
  }

  setPerformanceReport(`${path}.data-response-start`, timing);
}

/**
 * Completes the timing, recording the results on datalayer under performanceReport
 *
 * @function finaliseDatalayerTiming
 * @param {Object} path - datalayer path from info
 */
function finaliseDatalayerTiming(path) {
  if (!path) {
    flog("[ERROR] The path to set timing was not found.");
    return;
  }

  const timeEnd = getPerformanceTime();
  setPerformanceReport(`${path}.data-response-end`, timeEnd);

  const timeStart = nested.get(
    window.commercialData.performanceReport[path],
    "dataResponseStart"
  );
  const timeTotal = parseInt((timeEnd - timeStart).toFixed(2));
  setPerformanceReport(`${path}.data-response-total`, timeTotal);
}

/**
 * Update commercial data with adblocker detection info
 * @function setAdblockerStatus
 * @param {Event} e the 'adblocker.detected' event
 */
function setAdblockerStatus(e) {
  setInfo(
    "adblocker." + (e.data.adblockerDetector || "enabled"),
    e.data.adblockerEnabled
  );
  if (e.data && e.data.adblockerTracking) {
    events.fire(window, "analytics.track", e.data);
  }
}

/**
 * Gets the type of the current page from the data layer
 * - may return undefined if datalayer not available
 *
 * @param access - datalayer access function (optiional)
 * @returns {*} Page Type if available
 */
function getPageType(access) {
  let pageType;
  access = access || (window.TMDATA || {}).access;
  if (access && "function" === typeof access) {
    pageType =
      access("article.attributes.objectType") ||
      access("page.attributes.pageType");
  }
  return pageType;
}

function setPageInfo(e) {
  const access = e.data.access;
  setInfo("url", access("page.info.url"));
  setInfo("page-type", getPageType(access));
  setInfo("section", access("page.attributes.subSectionOne"));
  setInfo("sub-section", access("page.attributes.subSectionTwo"));
}

/**
 * Setup for the event listeners.
 * @function setupListeners
 */
function setupListeners() {
  events.listen(window, "breakpointchange", breakpointChangeListener);
  events.listen(window, "adblocker.detected", setAdblockerStatus);
  events.listen(document.body, "dataLayer.ready", setPageInfo);
}

/**
 * Handles when the breakpointchange event is fired
 * - stops timings for reloading elements, so they don't pollute the data
 *
 * @function breakpointChangeListener
 */
function breakpointChangeListener() {
  breakpointChanged = true;
}

/**
 * Set a custom metric on an individual component instance. Will be automatically consolidated.
 * @function setMetric
 * @param {Object} el The DOM element being measured
 * @param {string} name The name of the metric
 * @param {number} value The value of the metric
 */
function setMetric(el, name, value) {
  if (getFeatureValue("reduceJunkInDataLayer", "features", "bool")) {
    return;
  }
  flog("setMetric", el?.id, name, value);
  const type = el.dataset.type;
  const metrics = window.commercialData.metrics;
  const componentKey = type + ".data." + toCamelCase(el.id) + "." + name;
  const consolidatedKey = type + ".data." + name;

  let componentMetric = nested.get(metrics.components, componentKey);
  let consolidatedMetric = nested.get(metrics.consolidated, consolidatedKey);

  if (!componentMetric) {
    componentMetric = nested.set(metrics.components, componentKey, 0);
  }
  if (!consolidatedMetric) {
    consolidatedMetric = nested.set(metrics.consolidated, consolidatedKey, 0);
  }

  nested.set(metrics.components, componentKey, componentMetric + value);
  nested.set(metrics.consolidated, consolidatedKey, consolidatedMetric + value);

  updateDataLayer(true);
}

/**
 * Set a property on the info section of commercialData - usually page level, something thats not suitable as a metric or timer
 * @param {string} name - can be nested using dot syntax
 * @param {*} value - the value to assign
 * @param {boolean} canItWait - [optional, defaults to false] whether data is needed urgently in the dataLayer (false), or it can wait a little while (true)
 */
function setInfo(name, value, canItWait = false) {
  flog("setInfo", name, value);
  nested.set(window.commercialData.info, toCamelCase(name), value);
  updateDataLayer(canItWait);
}

/**
 * Get a property on the info section of commercialData, usually page level
 * @param {string} name - can be nested using dot syntax
 */
function getInfo(name) {
  let info = nested.get(window.commercialData.info, toCamelCase(name));
  return info;
}

/**
 * Report timing information to datalayer
 */
function setScriptTimer(name, value) {
  flog("setScriptTimer", name, value);
  nested.set(
    window.commercialData.performanceReport.scriptTimers,
    toCamelCase(name),
    value
  );
  updateDataLayer(true);
}

/**
 * Report timing information to datalayer
 */
function setPerformanceReport(name, value) {
  flog("setPerformanceReport", name, value);
  nested.set(window.commercialData.performanceReport, toCamelCase(name), value);
  updateDataLayer(true);
}

/**
 * Update the datalayer with the information in the localstorage
 * @function init
 */
function init() {
  if (window.commercialData) {
    return;
  }
  breakpointChanged = false;

  window.commercialData = window.commercialData || {
    performanceReport: {
      consolidated: {},
      components: {},
      scriptTimers: {},
    },
    metrics: {
      consolidated: {},
      components: {},
    },
    info: {},
  };

  // Set up event listeners (e.g breakpoint change)
  setupListeners();
}

init();

export default {
  finaliseElementTiming,
  finaliseDatalayerTiming,
  getPageType,
  getPerformanceStats,
  getPerformanceTime,
  init,
  initialiseDatalayerTiming,
  initialiseElementTiming,
  performanceTimeReport,
  toCamelCase,
  updateViewability,
  setScriptTimer,
  setCustomTimer,
  setInfo,
  getInfo,
  setMetric,
  setPerformanceReport,
};
